/**
 * Pin payload sanitizer — tolerant-but-honest deserialization of pinned
 * workspace payloads read back from disk, before they're used to open tabs.
 *
 * Unlike a lenient parser that silently coerces invalid data into a
 * "close enough" shape (the F8 bug class — an unknown `jsonSubOperator`
 * degrading to `has_property` and silently changing query semantics), this
 * module DROPS invalid filter conditions and COUNTS them so the caller can
 * tell the user "N filters could not be restored" (F8/G9). The notebook-cell
 * half of the sanitizer (unknown cell types → inert markdown, invalid chart
 * configs stripped — G10) lives in `notebookCellSanitizer.ts`, re-exported
 * here so callers only need this one module.
 *
 * Pure module: no IPC, no store, no side effects. Upstream envelope/shape
 * validation (`validatePinnedObjectRef` in `electron/metaInfo/connectionMetaInfo.ts`)
 * already guarantees `filters`/`sorting`/`cells` are arrays before this runs —
 * this module is the deep per-condition pass.
 */

import type {
  FilterCondition,
  FilterGroup,
  JsonSubOperator,
} from '../../../workspace/core/types/workspaceTypes';
import type { PinnedTableViewPayload } from '../../../../types/electron';
import {
  jsonSubOperatorRequiresPath,
  jsonSubOperatorRequiresValue,
} from '../../../workspace/common/utils/filterUtils';

export { sanitizeNotebookCells, type PinnedNotebookCells } from './notebookCellSanitizer';

// ============================================================================
// Filter condition validity
// ============================================================================

type Arity = 'none' | 'one' | 'two';

/**
 * Every literal in `FilterCondition['operator']` (workspaceTypes.ts) MUST
 * have an entry here — the `Record<FilterCondition['operator'], Arity>` type
 * makes the compiler enforce it, so a missing map entry is a build error, not
 * a silent runtime gap. An operator absent at runtime (bad/old data) is
 * handled defensively in `conditionOk` below, not here.
 */
const OPERATOR_ARITY: Record<FilterCondition['operator'], Arity> = {
  '=': 'one',
  '<': 'one',
  '<=': 'one',
  '>': 'one',
  '>=': 'one',
  LIKE: 'one',
  CONTAINS: 'one',
  '!=': 'one',
  BETWEEN: 'two',
  // "in list" / "not in list": `value` is the list (an empty one applies
  // nothing, like an empty `enum` multi-select).
  IN: 'one',
  'NOT IN': 'one',
  'IS NULL': 'none',
  'IS NOT NULL': 'none',
  lookup: 'one',
  array_contains: 'one',
  array_not_contains: 'one',
  array_is_empty: 'none',
  array_is_not_empty: 'none',
  array_length_eq: 'one',
  array_length_neq: 'one',
  array_length_gt: 'one',
  array_length_lt: 'one',
  array_length_gte: 'one',
  array_length_lte: 'one',
  // `json`'s real arity is driven by `jsonSubOperator`, not a fixed
  // value/value2 shape (some sub-ops need a value, some need only `path`,
  // some need neither) — handled per-sub-operator via `jsonConditionOk`
  // (routing through the filterUtils helpers) instead of the generic check.
  json: 'none',
  enum: 'one',
  // `spatial`'s operands live in dedicated fields (spatialSubOperator + bbox /
  // point / distanceMeters / polygon / spatialContext), never the generic
  // value/value2 shape — so its generic arity is `'none'` and the field-level
  // validity is enforced by `coerceFilterGroups` on restore, mirroring `json`.
  spatial: 'none',
};

/**
 * Compiler-exhaustiveness shim. Every `JsonSubOperator` literal must appear as
 * a key here — `satisfies Record<JsonSubOperator, true>` turns a missing
 * literal into a build error, not a silent runtime gap. Crucially, the path /
 * value REQUIREMENTS are NOT hand-authored here: a local table used to drift
 * from the grid UI's own contract (the K1/F8 bug class). Instead the checks
 * route through the app's canonical filterUtils helpers
 * `jsonSubOperatorRequiresPath` / `jsonSubOperatorRequiresValue`, which are the
 * arbiter of a valid condition. Where a helper demands a path/value the emit
 * intuition wouldn't, we over-drop — never silently keep a condition the grid
 * UI would itself reject.
 */
const JSON_SUB_OPERATOR_LITERALS = {
  has_property: true,
  not_has_property: true,
  path_eq: true,
  path_ieq: true,
  path_neq: true,
  path_is_null: true,
  path_is_not_null: true,
  path_contains: true,
  path_gt: true,
  path_gte: true,
  path_lt: true,
  path_lte: true,
  is_empty: true,
  is_not_empty: true,
  is_valid: true,
  is_not_valid: true,
  any_eq: true,
  any_ieq: true,
  any_contains: true,
  any_gt: true,
  any_gte: true,
  any_lt: true,
  any_lte: true,
  all_eq: true,
  all_gt: true,
  all_gte: true,
  all_lt: true,
  all_lte: true,
  property_lookup: true,
  any_lookup: true,
  all_lookup: true,
  property_enum: true,
  any_enum: true,
  all_enum: true,
} satisfies Record<JsonSubOperator, true>;

/**
 * Derived membership set — a Set (not an `in` check) so inherited prototype
 * keys like `toString` can't masquerade as known sub-operators.
 */
const KNOWN_JSON_SUB_OPERATORS: ReadonlySet<string> = new Set(
  Object.keys(JSON_SUB_OPERATOR_LITERALS),
);

function isKnownJsonSubOperator(value: unknown): value is JsonSubOperator {
  return typeof value === 'string' && KNOWN_JSON_SUB_OPERATORS.has(value);
}

/** `undefined` / `null` / `''` all count as "no operand supplied". */
function isEmptyOperand(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// ---- Optional-field TYPE guards (L1) ---------------------------------------
// The emptiness checks alone are not enough: a `path: {}` is "non-empty" here,
// but `deserializeTab` later strips the non-string path while KEEPING the
// condition → silent root-path fallback (the F8 class again). Every optional
// field the sanitizer inspects must also be the right TYPE; a wrong-typed
// field drops the whole condition.

/** `value` must be a legal FilterCondition operand when present. */
function isValidValueType(value: unknown): boolean {
  if (value === undefined || value === null) return true; // absence handled by arity checks
  const t = typeof value;
  if (t === 'string' || t === 'number' || t === 'boolean') return true;
  if (value instanceof Date) return true;
  // A list (enum multi-select, "in list") holds text and numeric keys.
  if (Array.isArray(value)) return value.every((v) => typeof v === 'string' || typeof v === 'number');
  return false;
}

/** `value2` (BETWEEN upper bound) must be string | number | Date when present. */
function isValidValue2Type(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  const t = typeof value;
  return t === 'string' || t === 'number' || value instanceof Date;
}

/** `path` must be a string when present (regardless of the sub-op's needs). */
function isValidPathType(path: unknown): boolean {
  return path === undefined || path === null || typeof path === 'string';
}

/**
 * Validity of a condition carrying a JSON sub-operator. The `json` operator's
 * entire validity is defined by its sub-operator — a missing/unknown sub-op
 * silently degrades to `has_property` downstream (filterUtils defaults it),
 * changing query semantics (F8) — so a known sub-op is REQUIRED, and its path /
 * value needs come from the canonical helpers (K1).
 */
function jsonConditionOk(condition: FilterCondition): boolean {
  if (!isKnownJsonSubOperator(condition.jsonSubOperator)) return false; // F8: drop, never degrade
  if (jsonSubOperatorRequiresPath(condition.jsonSubOperator) && isEmptyOperand(condition.path)) {
    return false; // G9: no silent `$`
  }
  if (jsonSubOperatorRequiresValue(condition.jsonSubOperator) && isEmptyOperand(condition.value)) {
    return false; // G9: no `col = NULL`
  }
  return true;
}

function conditionOk(condition: FilterCondition): boolean {
  // Widened lookup: `condition.operator` is typed as the known union, but the
  // actual runtime value came from disk and may not conform (that's exactly
  // what this function guards against).
  const arity = (OPERATOR_ARITY as Record<string, Arity | undefined>)[condition.operator];
  if (arity === undefined) return false; // unrecognized operator

  // L1: wrong-TYPED optional fields drop the condition before any semantic
  // checks — an emptiness check alone would let `path: {}` / `value: {}`
  // survive and degrade downstream.
  if (!isValidPathType(condition.path)) return false;
  if (!isValidValueType(condition.value)) return false;
  if (!isValidValue2Type(condition.value2)) return false;

  // K1: a `json` condition is valid iff its sub-operator is, regardless of the
  // generic value/value2 arity (its arity is `'none'` but real requirements are
  // sub-op-driven).
  if (condition.operator === 'json') {
    return jsonConditionOk(condition);
  }

  if (arity !== 'none' && isEmptyOperand(condition.value)) return false; // G9: no `= NULL`
  if (arity === 'two' && isEmptyOperand(condition.value2)) return false; // G9: BETWEEN needs value2

  // A non-json condition may still carry a jsonSubOperator from odd/old data;
  // when present it must satisfy the same json-condition contract.
  if (condition.jsonSubOperator !== undefined) {
    return jsonConditionOk(condition);
  }

  return true;
}

/**
 * Sanitizes a pinned Table Explorer view payload: drops filter conditions
 * that can't be honestly restored (unknown operator/sub-operator, missing a
 * required operand), removing any group left empty afterward. `sorting` and
 * `selectedColumns` pass through untouched — column existence against the
 * live schema is deferred reconciliation (Task 14), not this module's job.
 */
export function sanitizeTableViewPayload(
  payload: PinnedTableViewPayload,
): { payload: PinnedTableViewPayload; droppedCount: number } {
  let droppedCount = 0;

  const filters: FilterGroup[] = [];
  // Widened iteration: upstream validation guarantees `filters` is an array, but
  // its ELEMENTS came from disk and may be malformed — guard each structurally
  // (K1) so a bad group/condition drops-and-counts instead of throwing at this
  // tolerant boundary.
  for (const group of payload.filters as unknown[]) {
    if (!isRecord(group) || !Array.isArray(group.conditions) || typeof group.field !== 'string') {
      droppedCount++; // unrestorable group shape
      continue;
    }

    const conditions: FilterCondition[] = [];
    for (const condition of group.conditions as unknown[]) {
      if (!isRecord(condition) || typeof condition.operator !== 'string') {
        droppedCount++; // non-object / operator-less condition
        continue;
      }
      if (!conditionOk(condition as FilterCondition)) {
        droppedCount++;
        continue;
      }
      conditions.push(condition as FilterCondition);
    }

    if (conditions.length > 0) {
      filters.push({ ...(group as FilterGroup), conditions });
    }
  }

  return { payload: { ...payload, filters }, droppedCount };
}
